import { BookOpen, Check } from "lucide-react";
import { t } from "@/lib/i18n";

const Examples = ({ items, light }) => (
  <ul className="space-y-2 mt-3">
    {items.map((e) => (
      <li key={e} className={`flex gap-2 text-sm leading-relaxed ${light ? "text-white/80" : "text-muted-ink"}`}>
        <Check size={16} className={`shrink-0 mt-0.5 ${light ? "text-[#8FD3C6]" : "text-teal"}`} />{e}
      </li>
    ))}
  </ul>
);

const PairBlend = ({ pair, profile, lang }) => (
  <div className="mt-8 rounded-xl border border-white/15 p-6 md:p-7 grid md:grid-cols-2 gap-8" data-testid="disc-pair-explanation">
    <div>
      <p className="eyebrow !text-[#8FD3C6]">{t(lang, "blend")} · {profile}</p>
      <h3 className="font-display text-2xl font-semibold mt-2" data-testid="disc-pair-title">{pair.title}</h3>
      <p className="text-white/80 leading-relaxed mt-3">{pair.description}</p>
      <p className="text-sm text-white/70 mt-4"><strong className="text-white">{t(lang, "biblical")}</strong> {pair.biblical}</p>
    </div>
    <div>
      <p className="font-display font-semibold">{t(lang, "blendLooks")}</p>
      <Examples items={pair.examples} light />
    </div>
  </div>
);

export const ResultExplain = ({ v, reference, lang = "en" }) => {
  const disc = reference.disc.find((d) => d.key === v.disc_primary);
  const pair = reference.pairs?.[v.disc_profile];
  const topGifts = v.gift_scores.slice(0, 3).map((g) => reference.gifts.find((x) => x.key === g.key)).filter(Boolean);

  return (
    <section className="space-y-6" data-testid="result-explanations">
      <div className="bg-navy text-white rounded-2xl p-8 md:p-10" data-testid="disc-explanation">
        <p className="eyebrow !text-[#8FD3C6]">{t(lang, "personalityMeans")}</p>
        <h2 className="font-display text-3xl font-semibold mt-3">
          {t(lang, "youre")} <span className="font-accent text-[#8FD3C6]">{disc.name}</span>{pair && <> · {pair.title}</>}.
        </h2>
        <div className="grid md:grid-cols-2 gap-10 mt-6">
          <div>
            <p className="text-white/85 leading-relaxed">{disc.explanation}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {disc.strengths.map((s) => <span key={s} className="text-xs border border-white/20 rounded-full px-3 py-1">{s}</span>)}
            </div>
            <p className="text-sm text-white/70 mt-5"><strong className="text-white">{t(lang, "biblical")}</strong> {disc.biblical}</p>
          </div>
          <div className="bg-white/[0.06] rounded-xl p-6">
            <p className="font-display font-semibold">{t(lang, "onTeam")}</p>
            <Examples items={disc.examples} light />
            <p className="text-sm text-white/70 mt-5"><strong className="text-white">{t(lang, "watch")}</strong> {disc.watch}</p>
          </div>
        </div>
        {pair && <PairBlend pair={pair} profile={v.disc_profile} lang={lang} />}
      </div>

      {topGifts.length > 0 && <div className="card-soft p-8 md:p-10" data-testid="gift-explanations">
        <p className="eyebrow">{t(lang, "giftsMean")}</p>
        <h2 className="font-display text-navy text-3xl font-semibold mt-3">{t(lang, "yourTop")} <span className="font-accent text-teal">{t(lang, "spiritualGifts")}</span></h2>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {topGifts.map((g, i) => (
            <div key={g.key} className="bg-paper rounded-xl p-6" data-testid={`gift-explain-${i}`}>
              <span className="font-mono-dt text-xs text-teal">#{i + 1}</span>
              <h3 className="font-display text-navy text-xl font-semibold mt-1">{g.name}</h3>
              <p className="text-muted-ink text-sm leading-relaxed mt-2">{g.description}</p>
              <p className="flex items-center gap-1.5 text-xs text-teal font-medium mt-4"><BookOpen size={14} /> {g.scripture}</p>
              <p className="text-navy text-sm font-medium mt-4">{t(lang, "showsUp")}</p>
              <Examples items={g.examples} />
            </div>
          ))}
        </div>
      </div>}
    </section>
  );
};
